/**
 * 本地 / 局域网开发环境预设（H5 dev server :8000，后端 API :8080）。
 * H5 源站与 API 可指向不同机器（例如 H5 在本机、API 在同事机）。
 */

/** H5 开发服务端口 */
export const DEV_H5_PORT = 8000;

/** 后端 API 端口 */
export const DEV_API_PORT = 8080;

/** `roc(家)` 家里局域网 IP */
export const DEV_HOST_ROC_HOME = '192.168.1.9';

/**
 * App `envRoc2` / 另一台家里机：H5 :8000 + API :8080 同机。
 * 未列入时访问该 IP 会误落到 testy6，出现「网络错误」。
 */
export const DEV_HOST_ROC_HOME_2 = '192.168.1.10';

/** `roc(家)` 公司局域网 IP */
export const DEV_HOST_ROC_COMPANY = '192.168.31.198';

/**
 * `roc(家)` 可匹配的 H5 hostname（家里 + 公司）。
 * 访问其中任一 IP 的 :8000 时，API 自动指向同机 :8080。
 */
export const DEV_HOSTS_ROC_HOME: readonly string[] = [
  DEV_HOST_ROC_HOME,
  DEV_HOST_ROC_HOME_2,
  DEV_HOST_ROC_COMPANY,
] as const;

/** `roc` 局域网 IP */
export const DEV_HOST_ROC = '192.168.31.198';

/** `roc2` 局域网 IP */
export const DEV_HOST_ROC2 = '192.168.31.204';

/** `wolf` 局域网 IP */
export const DEV_HOST_WOLF = '192.168.31.161';

/** 本机 hostname */
export const DEV_HOST_LOCAL = 'localhost';

/** 生产环境 API 根地址 */
export const DEV_API_BASE_PROD = 'https://api.sduck.net';

/** 测试环境 API 根地址（与 request.ts 默认 fallback 一致） */
export const DEV_API_BASE_TEST = 'https://testy6.sduck.net';

/** 单机开发环境：展示名 + H5 页 host + H5 源站 + API 根地址 */
export type DevEnvPreset = {
  label: string;
  /** 浏览器打开 H5 时的 hostname，用于按地址匹配预设 */
  h5Host: string;
  h5Origin: string;
  apiBaseUrl: string;
};

/**
 * 由 host 拼装 H5 源站 URL。
 * @param host hostname（IP 或 localhost）
 */
export function devH5Origin(host: string): string {
  return `http://${host}:${DEV_H5_PORT}`;
}

/**
 * 由 host 拼装 API 根地址。
 * @param host hostname（IP 或 localhost）
 */
export function devApiBaseUrl(host: string): string {
  return `http://${host}:${DEV_API_PORT}`;
}

/**
 * 组装开发环境预设；H5 与 API 默认同机局域网，可单独指定 API 根地址。
 * @param label 展示名
 * @param h5Host H5 页 hostname
 * @param apiBaseUrl API 根地址，省略时为同机 `devApiBaseUrl(h5Host)`
 */
export function buildDevEnvPreset(
  label: string,
  h5Host: string,
  apiBaseUrl: string = devApiBaseUrl(h5Host),
): DevEnvPreset {
  return {
    label,
    h5Host,
    h5Origin: devH5Origin(h5Host),
    apiBaseUrl,
  };
}

/** 开发环境快捷项 */
export const DEV_ENV_PRESETS: readonly DevEnvPreset[] = [
  buildDevEnvPreset('roc(家)', DEV_HOST_ROC_HOME),
  buildDevEnvPreset('roc', DEV_HOST_ROC),
  buildDevEnvPreset('roc2', DEV_HOST_ROC2),
  buildDevEnvPreset('wolf', DEV_HOST_WOLF, DEV_API_BASE_TEST),
  buildDevEnvPreset('本地', DEV_HOST_LOCAL, DEV_API_BASE_TEST),
] as const;

/**
 * 按 label 查找预设（未命中返回 undefined）。
 * @param label 预设展示名
 */
export function findDevEnvPresetByLabel(label: string): DevEnvPreset | undefined {
  const key = label.trim();
  return DEV_ENV_PRESETS.find((p) => p.label === key);
}

/**
 * 按当前 H5 页 hostname 查找预设（未命中返回 undefined）。
 * `roc(家)` 支持家里与公司多个 IP，API 与当前页 hostname 同机。
 * @param hostname `window.location.hostname` 或同等值
 */
export function findDevEnvPresetByHostname(hostname: string): DevEnvPreset | undefined {
  const key = hostname.trim();
  if ((DEV_HOSTS_ROC_HOME as readonly string[]).includes(key)) {
    return buildDevEnvPreset('roc(家)', key);
  }
  return DEV_ENV_PRESETS.find((p) => p.h5Host === key);
}

/** `roc(家)` 预设，供脚本 / 注释引用 */
export const DEV_ENV_ROC_HOME = DEV_ENV_PRESETS[0];

/** `roc` 预设 */
export const DEV_ENV_ROC = DEV_ENV_PRESETS[1];

/** `roc2` 预设 */
export const DEV_ENV_ROC2 = DEV_ENV_PRESETS[2];

/** `wolf` 预设 */
export const DEV_ENV_WOLF = DEV_ENV_PRESETS[3];

/** `本地` 预设 */
export const DEV_ENV_LOCAL = DEV_ENV_PRESETS[4];
